import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n';

export interface AttribValue {
  value_id: number;
  value_text: string;
  value_sort: number;
  doc_count: number;
}

export interface Attrib {
  attrib_id: number;
  attrib_name: string;
  attrib_uid: string;
  attrib_type: string;
  attrib_unit: string;
  attrib_sort: number;
  values: AttribValue[];
  range_min: number | null;
  range_max: number | null;
}

export interface FilterCounts {
  sale: number;
  new: number;
  saleout: number;
  top: number;
}

export type FlagKey = 'sale' | 'new' | 'saleout' | 'top';

export type DeliveryCounts = { [key: string]: number };

interface CategorySidebarFiltersProps {
  attribs: Attrib[];
  selectedParameters: string[];
  onParametersChange: (parameters: string[]) => void;
  priceBounds: { min: number; max: number };
  priceValue: { min: string; max: string };
  onPriceChange: (min: string, max: string) => void;
  selectedDeliveryDays: string[];
  deliveryCounts: DeliveryCounts | null;
  deliveryCountsBase: DeliveryCounts | null;
  onDeliveryDaysChange: (deliveryDays: string[]) => void;
  selectedFlags: string[];
  flagCounts: FilterCounts | null;
  flagCountsBase: FilterCounts | null;
  onFlagsChange: (flags: string[]) => void;
  defaultCollapsed?: boolean;
}

const FLAG_OPTIONS: Array<{ key: FlagKey; label: string }> = [
  { key: 'sale', label: __('Akcia') },
  { key: 'new', label: __('Novinka') },
];

export const DELIVERY_OPTIONS: Array<{ key: string; label: string }> = [
  { key: '0-2', label: __('0-2 dni') },
  { key: '2-3', label: __('2-3 dni') },
  { key: '3-4', label: __('3-4 dni') },
];

const VALUES_COLLAPSED = 5;

/* ------------------------------------------------------------------ layout */

const Wrapper = styled.div`
  background: ${({ theme }) => theme.color.white};
  padding: 0 ${rem(16)};
`;

const Section = styled.div`
  padding: ${rem(16)} 0;
  border-bottom: ${rem(1)} solid ${({ theme }) => theme.color.gray83};
  &:first-child {
    border-top: ${rem(1)} solid ${({ theme }) => theme.color.gray83};
  }
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
`;

const SectionTitle = styled.span`
  font-weight: 700;
  font-size: ${rem(16)};
  line-height: ${rem(24)};
  color: ${({ theme }) => theme.color.black};
`;

const CollapseIcon = styled.span<{ isOpen: boolean }>`
  position: relative;
  width: ${rem(24)};
  height: ${rem(24)};
  flex-shrink: 0;
  &:before,
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: ${rem(12)};
    height: ${rem(1.5)};
    background: ${({ theme }) => theme.color.black};
    transform: translate(-50%, -50%);
  }
  /* vertical bar becomes a "+" when collapsed */
  &:after {
    transform: translate(-50%, -50%) rotate(90deg);
    ${({ isOpen }) =>
      isOpen &&
      css`
        opacity: 0;
      `}
  }
`;

const SectionBody = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  margin-top: ${rem(16)};
`;

/* ------------------------------------------------------------- checkbox row */

const Row = styled.label<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  padding: ${rem(2)} 0;
  user-select: none;
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      opacity: 0.4;
    `}
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const Box = styled.span<{ isChecked: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: ${rem(20)};
  height: ${rem(20)};
  border-radius: ${rem(2)};
  box-sizing: border-box;
  ${({ isChecked, theme }) =>
    isChecked
      ? css`
          background: ${theme.color.primary};
          border: ${rem(1)} solid ${theme.color.primary};
        `
      : css`
          background: ${theme.color.white};
          border: ${rem(1)} solid ${theme.color.black};
        `};
  &:after {
    content: '';
    position: absolute;
    display: ${({ isChecked }) => (isChecked ? 'block' : 'none')};
    left: ${rem(6)};
    top: ${rem(2)};
    width: ${rem(6)};
    height: ${rem(11)};
    border: solid ${({ theme }) => theme.color.white};
    border-width: 0 ${rem(2)} ${rem(2)} 0;
    transform: rotate(45deg);
    box-sizing: border-box;
  }
`;

const LabelGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
`;

const RowLabel = styled.span<{ isChecked: boolean }>`
  font-size: ${rem(14)};
  line-height: ${rem(24)};
  color: ${({ theme }) => theme.color.black};
  font-weight: ${({ isChecked }) => (isChecked ? 600 : 400)};
`;

const Count = styled.span`
  font-size: ${rem(14)};
  line-height: ${rem(24)};
  color: #666666;
`;

const ValueList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ShowAllButton = styled.button`
  align-self: flex-start;
  margin-top: ${rem(8)};
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: ${rem(14)};
  font-weight: 600;
  line-height: ${rem(20)};
  text-decoration: underline;
  color: ${({ theme }) => theme.color.primary};
  cursor: pointer;
`;

/* --------------------------------------------------------------- price box */

const PriceInputs = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(16)};
`;

const PriceField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`;

const PriceFieldLabel = styled.span`
  font-size: ${rem(14)};
  line-height: ${rem(24)};
  color: ${({ theme }) => theme.color.black};
`;

const PriceInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  padding: ${rem(7)} ${rem(12)};
  background: ${({ theme }) => theme.color.white};
  border: ${rem(1)} solid ${({ theme }) => theme.color.gray70};
  border-radius: ${rem(4)};
  &:focus-within {
    border-color: ${({ theme }) => theme.color.primary};
  }
`;

const PriceInput = styled.input`
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  padding: 0;
  font-size: ${rem(15)};
  font-weight: 400;
  line-height: ${rem(24)};
  color: ${({ theme }) => theme.color.black};
  background: transparent;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const PriceCurrency = styled.span`
  flex-shrink: 0;
  font-size: ${rem(15)};
  line-height: ${rem(24)};
  color: ${({ theme }) => theme.color.gray38};
`;

/* dual-range slider */
const SliderWrap = styled.div`
  position: relative;
  height: ${rem(16)};
  margin-top: ${rem(16)};
`;

const SliderTrack = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: ${rem(3)};
  transform: translateY(-50%);
  background: ${({ theme }) => theme.color.gray83};
  border-radius: ${rem(2)};
`;

const SliderFill = styled.div`
  position: absolute;
  top: 50%;
  height: ${rem(3)};
  transform: translateY(-50%);
  background: ${({ theme }) => theme.color.primary};
  border-radius: ${rem(2)};
`;

const RangeInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${rem(16)};
  margin: 0;
  background: none;
  pointer-events: none;
  -webkit-appearance: none;
  appearance: none;
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    pointer-events: auto;
    width: ${rem(16)};
    height: ${rem(16)};
    border-radius: 50%;
    background: ${({ theme }) => theme.color.primary};
    border: none;
    cursor: pointer;
  }
  &::-moz-range-thumb {
    pointer-events: auto;
    width: ${rem(16)};
    height: ${rem(16)};
    border-radius: 50%;
    background: ${({ theme }) => theme.color.primary};
    border: none;
    cursor: pointer;
  }
  &::-webkit-slider-runnable-track {
    background: transparent;
  }
  &::-moz-range-track {
    background: transparent;
  }
`;

/* ------------------------------------------------------------- range control */

const RangeControl: React.FC<{
  bounds: { min: number; max: number };
  value: { min: string; max: string };
  unit?: string;
  /** emit '' for a side that sits at its bound (i.e. "no limit") */
  blankAtBounds?: boolean;
  onChange: (min: string, max: string) => void;
}> = ({ bounds, value, unit = '€', blankAtBounds = true, onChange }) => {
  const hasRange = bounds.max > bounds.min;

  const [local, setLocal] = React.useState<{ min: number; max: number }>({
    min: value.min !== '' ? parseFloat(value.min) : bounds.min,
    max: value.max !== '' ? parseFloat(value.max) : bounds.max,
  });

  React.useEffect(() => {
    setLocal({
      min: value.min !== '' ? parseFloat(value.min) : bounds.min,
      max: value.max !== '' ? parseFloat(value.max) : bounds.max,
    });
  }, [value.min, value.max, bounds.min, bounds.max]);

  const clamp = (n: number) => Math.min(bounds.max, Math.max(bounds.min, n));

  const commit = (min: number, max: number) => {
    const safeMin = Number.isFinite(min) ? min : bounds.min;
    const safeMax = Number.isFinite(max) ? max : bounds.max;
    const lo = clamp(Math.min(safeMin, safeMax));
    const hi = clamp(Math.max(safeMin, safeMax));
    onChange(
      blankAtBounds && lo <= bounds.min ? '' : String(Math.round(lo)),
      blankAtBounds && hi >= bounds.max ? '' : String(Math.round(hi)),
    );
  };

  const pct = (n: number) =>
    hasRange ? ((clamp(n) - bounds.min) / (bounds.max - bounds.min)) * 100 : 0;

  return (
    <>
      <PriceInputs>
        <PriceField>
          <PriceFieldLabel>{__('od')}</PriceFieldLabel>
          <PriceInputWrap>
            <PriceInput
              type="number"
              value={Number.isFinite(local.min) ? local.min : ''}
              min={bounds.min}
              max={bounds.max}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setLocal((p) => ({ ...p, min: v }));
              }}
              onBlur={() => commit(local.min, local.max)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit(local.min, local.max);
              }}
            />
            {unit ? <PriceCurrency>{unit}</PriceCurrency> : null}
          </PriceInputWrap>
        </PriceField>
        <PriceField>
          <PriceFieldLabel>{__('do')}</PriceFieldLabel>
          <PriceInputWrap>
            <PriceInput
              type="number"
              value={Number.isFinite(local.max) ? local.max : ''}
              min={bounds.min}
              max={bounds.max}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setLocal((p) => ({ ...p, max: v }));
              }}
              onBlur={() => commit(local.min, local.max)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit(local.min, local.max);
              }}
            />
            {unit ? <PriceCurrency>{unit}</PriceCurrency> : null}
          </PriceInputWrap>
        </PriceField>
      </PriceInputs>

      {hasRange && (
        <SliderWrap>
          <SliderTrack />
          <SliderFill
            style={{
              left: `${pct(local.min)}%`,
              right: `${100 - pct(local.max)}%`,
            }}
          />
          <RangeInput
            type="range"
            min={bounds.min}
            max={bounds.max}
            value={clamp(local.min)}
            onChange={(e) => {
              const v = Math.min(parseFloat(e.target.value), local.max);
              setLocal((p) => ({ ...p, min: v }));
            }}
            onMouseUp={() => commit(local.min, local.max)}
            onTouchEnd={() => commit(local.min, local.max)}
            onKeyUp={() => commit(local.min, local.max)}
          />
          <RangeInput
            type="range"
            min={bounds.min}
            max={bounds.max}
            value={clamp(local.max)}
            onChange={(e) => {
              const v = Math.max(parseFloat(e.target.value), local.min);
              setLocal((p) => ({ ...p, max: v }));
            }}
            onMouseUp={() => commit(local.min, local.max)}
            onTouchEnd={() => commit(local.min, local.max)}
            onKeyUp={() => commit(local.min, local.max)}
          />
        </SliderWrap>
      )}
    </>
  );
};

/* ------------------------------------------------------------- main component */

const CategorySidebarFilters: React.FC<CategorySidebarFiltersProps> = ({
  attribs,
  selectedParameters,
  onParametersChange,
  priceBounds,
  priceValue,
  onPriceChange,
  selectedDeliveryDays,
  deliveryCounts,
  deliveryCountsBase,
  onDeliveryDaysChange,
  selectedFlags,
  flagCounts,
  flagCountsBase,
  onFlagsChange,
  defaultCollapsed = false,
}) => {
  const sectionKeys = React.useMemo(
    () => [
      'price',
      'delivery',
      'flags',
      ...attribs.map((a) => `attrib_${a.attrib_id}`),
    ],
    [attribs],
  );

  const [openSections, setOpenSections] = React.useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    sectionKeys.forEach((k) => {
      initial[k] = !defaultCollapsed;
    });
    return initial;
  });

  const isOpen = (key: string) => openSections[key] !== false;
  const toggle = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: prev[key] === false }));

  const [expandedAttribs, setExpandedAttribs] = React.useState<
    Record<number, boolean>
  >({});
  const toggleExpand = (attribId: number) =>
    setExpandedAttribs((prev) => ({ ...prev, [attribId]: !prev[attribId] }));

  /* attrib range local state */
  const [rangeValues, setRangeValues] = React.useState<
    Record<number, { min: string; max: string }>
  >(() => {
    const initial: Record<number, { min: string; max: string }> = {};
    for (const param of selectedParameters) {
      const m = param.match(/^(\d+):(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
      if (m) initial[parseInt(m[1])] = { min: m[2], max: m[3] };
    }
    return initial;
  });
  const isTermSelected = (attribId: number, valueId: number) =>
    selectedParameters.includes(`${attribId}-${valueId}`);

  const handleTermToggle = (attribId: number, valueId: number) => {
    const param = `${attribId}-${valueId}`;
    const newParams = selectedParameters.includes(param)
      ? selectedParameters.filter((p) => p !== param)
      : [...selectedParameters, param];
    onParametersChange(newParams);
  };

  const applyRangeFilter = (attribId: number, min: string, max: string) => {
    let newParams = selectedParameters.filter(
      (p) => !p.startsWith(`${attribId}:`),
    );
    if (min && max) newParams.push(`${attribId}:${min}-${max}`);
    onParametersChange(newParams);
  };

  /* flags */
  const handleFlagToggle = (key: FlagKey) => {
    const newFlags = selectedFlags.includes(key)
      ? selectedFlags.filter((f) => f !== key)
      : [...selectedFlags, key];
    onFlagsChange(newFlags);
  };

  const handleDeliveryToggle = (key: string) => {
    const newDeliveryDays = selectedDeliveryDays.includes(key)
      ? selectedDeliveryDays.filter((d) => d !== key)
      : [...selectedDeliveryDays, key];
    onDeliveryDaysChange(newDeliveryDays);
  };

  const showPrice = priceBounds.max > priceBounds.min;
  const baseHasDelivery = deliveryCountsBase
    ? DELIVERY_OPTIONS.some((o) => (deliveryCountsBase[o.key] || 0) > 0)
    : false;
  const showDelivery = baseHasDelivery || selectedDeliveryDays.length > 0;
  const baseHasFlags = flagCountsBase
    ? (flagCountsBase.sale || 0) > 0 || (flagCountsBase.new || 0) > 0
    : flagCounts != null;
  const showFlags = baseHasFlags || selectedFlags.length > 0;
  const hasAttribs = attribs && attribs.length > 0;

  if (!showPrice && !showDelivery && !showFlags && !hasAttribs) {
    return null;
  }

  return (
    <Wrapper>
      {showPrice && (
        <Section>
          <SectionHeader type="button" onClick={() => toggle('price')}>
            <SectionTitle>{__('Cena')}</SectionTitle>
            <CollapseIcon isOpen={isOpen('price')} />
          </SectionHeader>
          <SectionBody isOpen={isOpen('price')}>
            <RangeControl
              bounds={priceBounds}
              value={priceValue}
              onChange={onPriceChange}
            />
          </SectionBody>
        </Section>
      )}

      {showDelivery && (
        <Section>
          <SectionHeader type="button" onClick={() => toggle('delivery')}>
            <SectionTitle>{__('Dĺžka dodania')}</SectionTitle>
            <CollapseIcon isOpen={isOpen('delivery')} />
          </SectionHeader>
          <SectionBody isOpen={isOpen('delivery')}>
            {DELIVERY_OPTIONS.map((option) => {
              const checked = selectedDeliveryDays.includes(option.key);
              const count = deliveryCounts
                ? deliveryCounts[option.key] || 0
                : null;
              const disabled = count === 0 && !checked;
              return (
                <Row key={option.key} isDisabled={disabled}>
                  <HiddenInput
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => handleDeliveryToggle(option.key)}
                  />
                  <Box isChecked={checked} />
                  <LabelGroup>
                    <RowLabel isChecked={checked}>{option.label}</RowLabel>
                    {deliveryCounts && (
                      <Count>({deliveryCounts[option.key] || 0})</Count>
                    )}
                  </LabelGroup>
                </Row>
              );
            })}
          </SectionBody>
        </Section>
      )}

      {showFlags && (
        <Section>
          <SectionHeader type="button" onClick={() => toggle('flags')}>
            <SectionTitle>{__('Filter')}</SectionTitle>
            <CollapseIcon isOpen={isOpen('flags')} />
          </SectionHeader>
          <SectionBody isOpen={isOpen('flags')}>
            {FLAG_OPTIONS.map((flag) => {
              const checked = selectedFlags.includes(flag.key);
              const count = flagCounts ? flagCounts[flag.key] || 0 : null;
              const disabled = count === 0 && !checked;
              return (
                <Row key={flag.key} isDisabled={disabled}>
                  <HiddenInput
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => handleFlagToggle(flag.key)}
                  />
                  <Box isChecked={checked} />
                  <LabelGroup>
                    <RowLabel isChecked={checked}>{flag.label}</RowLabel>
                    {flagCounts && <Count>({flagCounts[flag.key] || 0})</Count>}
                  </LabelGroup>
                </Row>
              );
            })}
          </SectionBody>
        </Section>
      )}

      {attribs.map((attrib) => {
        const key = `attrib_${attrib.attrib_id}`;
        const open = isOpen(key);
        const isRange = attrib.attrib_type === 'rozsah';
        return (
          <Section key={attrib.attrib_id}>
            <SectionHeader type="button" onClick={() => toggle(key)}>
              <SectionTitle>
                {attrib.attrib_name}
                {attrib.attrib_unit ? ` (${attrib.attrib_unit})` : ''}
              </SectionTitle>
              <CollapseIcon isOpen={open} />
            </SectionHeader>
            <SectionBody isOpen={open}>
              {isRange ? (
                <RangeControl
                  bounds={{
                    min:
                      attrib.range_min !== null ? Number(attrib.range_min) : 0,
                    max:
                      attrib.range_max !== null ? Number(attrib.range_max) : 0,
                  }}
                  value={rangeValues[attrib.attrib_id] || { min: '', max: '' }}
                  unit={attrib.attrib_unit || ''}
                  blankAtBounds={false}
                  onChange={(min, max) => {
                    setRangeValues((prev) => ({
                      ...prev,
                      [attrib.attrib_id]: { min, max },
                    }));
                    applyRangeFilter(attrib.attrib_id, min, max);
                  }}
                />
              ) : (
                <ValueList>
                  {(expandedAttribs[attrib.attrib_id]
                    ? attrib.values
                    : attrib.values.filter(
                        (v, i) =>
                          i < VALUES_COLLAPSED ||
                          isTermSelected(attrib.attrib_id, v.value_id),
                      )
                  ).map((val) => {
                    const checked = isTermSelected(
                      attrib.attrib_id,
                      val.value_id,
                    );
                    const disabled = val.doc_count === 0 && !checked;
                    return (
                      <Row key={val.value_id} isDisabled={disabled}>
                        <HiddenInput
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() =>
                            handleTermToggle(attrib.attrib_id, val.value_id)
                          }
                        />
                        <Box isChecked={checked} />
                        <LabelGroup>
                          <RowLabel isChecked={checked}>
                            {val.value_text}
                          </RowLabel>
                          <Count>({val.doc_count})</Count>
                        </LabelGroup>
                      </Row>
                    );
                  })}
                  {attrib.values.length > VALUES_COLLAPSED && (
                    <ShowAllButton
                      type="button"
                      onClick={() => toggleExpand(attrib.attrib_id)}
                    >
                      {expandedAttribs[attrib.attrib_id]
                        ? __('Zobraziť menej')
                        : __('Všetky parametre')}
                    </ShowAllButton>
                  )}
                </ValueList>
              )}
            </SectionBody>
          </Section>
        );
      })}
    </Wrapper>
  );
};

export default CategorySidebarFilters;
